/**
 * "Agents at work" gadget (Build): the AI agents in the project's presence now (the same live socket as the header's
 * avatars): who drives each, the file it writes, its step, idle when it writes nothing. "Notify me when it's done"
 * (vfs-service `POST /collab/agents/:id/watch`, as the editor's agent band offers it) on an agent at work, for an
 * editor or the owner; Messages to write to them. Presence off (not a shared project): the gadget is not offered.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { watchAgent } from '../../../services/CollabService';
import { agentRows } from '../../../utils/homeGadgets/build';
import { ParticipantMark } from '../../presence/Presence';
import VendorName from '../../sandbox/VendorName';
import GadgetCard, { Quiet, TabLink } from './GadgetCard';

export default function AgentsAtWork({ projectId, presence = null, canEdit = false, onOpenMessages = null }) {
  const rows = agentRows(presence);
  const [watching, setWatching] = useState({});
  const [failed, setFailed] = useState(null);
  const toggle = async(id) => {
    setFailed(null);
    try {
      const answer = await watchAgent(projectId, id, !watching[id]);
      setWatching((now) => ({ ...now, [id]: Boolean(answer?.watching) }));
    } catch (error) {
      setFailed(error.message || 'That could not be done now.');
    }
  };
  const connecting = !presence || presence.status === 'connecting';
  return (
    <GadgetCard links={<TabLink onClick={onOpenMessages}>Messages</TabLink>} meta={rows.length ? `${rows.length} here` : null} testId="agents-gadget" title="Agents at work">
      {rows.length === 0 ? (
        <Quiet testId="gadget-empty">{connecting ? 'Looking for agents…' : 'No AI agent is working in this project now.'}</Quiet>
      ) : (
        <ul className="gd-rows">
          {rows.map((agent) => (
            <li className="gd-row" data-testid={`agent-${agent.id}`} key={agent.id}>
              <ParticipantMark agent idle={agent.idle} initials="AI" label={agent.name} paused={agent.paused} size={20} slot={0} />
              <strong><VendorName name={agent.name} /></strong>
              <span className="gd-grow ph-muted">
                {agent.paused ? 'paused' : agent.idle ? 'idle' : `writing ${agent.file}${agent.files > 1 ? ` +${agent.files - 1}` : ''}`}
                {agent.step && !agent.idle ? ` · ${agent.step}` : ''}
              </span>
              {canEdit && !agent.idle && (
                <button aria-pressed={Boolean(watching[agent.id])} className="ph-link" data-testid={`agent-watch-${agent.id}`} onClick={() => toggle(agent.id)} type="button">
                  {watching[agent.id] ? 'You will be notified · undo' : 'Notify me when it’s done'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {failed && <p className="gd-error" role="alert">{failed}</p>}
    </GadgetCard>
  );
}

AgentsAtWork.propTypes = {
  canEdit: PropTypes.bool,
  onOpenMessages: PropTypes.func,
  // The project's presence (hooks/useProjectPresence): `{ status, you, people, agents }`.
  presence: PropTypes.object,
  projectId: PropTypes.string.isRequired,
};
